import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { saveAs } from 'file-saver';
import { buildCsv } from './csvExport';
import moment from 'moment';
import firebase from 'firebase/compat/app';
import { firebaseAuth } from '../../providers/AuthProvider';
import { db } from '../../firebase';
import {
  buildCustomerCareExportRows,
  clearCustomerCareDashboardSettings,
  CUSTOMER_CARE_DATE_RANGES,
  CUSTOMER_CARE_PAGE_SIZE_OPTIONS,
  CUSTOMER_CARE_SUBMISSION_FIELD_DEFINITIONS,
  CUSTOMER_CARE_TYPE_OPTIONS,
  DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS,
  formatCustomerCareAdditionalData,
  formatCustomerCareAddress,
  formatCustomerCareBoolean,
  formatCustomerCareDate,
  getCustomerCareSubmissionFieldValue,
  getFilteredCustomerCareSubmissions,
  hasDefaultCustomerCareDashboardSettings,
  normalizeCustomerCareDashboardSettings,
  readCustomerCareDashboardSettings,
  sortCustomerCareSubmissions,
  writeCustomerCareDashboardSettings,
} from './customerCareDashboard';

const reducer = (state, payload) => ({ ...state, ...payload });

const labelCls = 'block text-sm font-medium text-zinc-700 dark:text-zinc-300';
const inputCls =
  'mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white';
const primaryBtnCls =
  'inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100';
const secondaryBtnCls =
  'inline-flex items-center justify-center gap-2 rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700';
const iconBtnCls =
  'inline-flex size-9 items-center justify-center rounded-md bg-zinc-50 text-zinc-700 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-white/10 dark:hover:bg-zinc-700';
const alertCls =
  'rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-500/30 dark:text-red-300 dark:ring-red-500/20';
const thCls =
  'whitespace-nowrap px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400';
const tdCls = 'align-top px-4 py-2 text-sm text-zinc-900 dark:text-zinc-100';
const compactCellCls = 'min-h-10 max-h-10 overflow-hidden leading-5';
const detailLineCls = 'text-xs text-zinc-600 dark:text-zinc-300';
const detailLabelCls = 'font-semibold text-zinc-700 dark:text-zinc-200';
const detailValueCls = 'break-words text-zinc-900 dark:text-zinc-100';

const sentToCapOptions = [
  { value: 'all', label: 'Any' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const defaultSortDirForKey = (key) => (key === 'createdAt' ? 'desc' : 'asc');

const SortIndicator = ({ active, dir }) => {
  if (!active) {
    return (
      <ChevronUpDownIcon
        className="size-3 text-zinc-300 dark:text-zinc-600"
        aria-hidden="true"
      />
    );
  }

  return dir === 'asc' ? (
    <ChevronUpIcon className="size-3" aria-hidden="true" />
  ) : (
    <ChevronDownIcon className="size-3" aria-hidden="true" />
  );
};

const hasDetailValue = (value) => value !== undefined && value !== null && value !== '';

const DetailLine = ({ label, value }) => {
  if (!hasDetailValue(value)) return null;

  return (
    <div className={detailLineCls}>
      <span className={detailLabelCls}>{label}:</span>{' '}
      <span className={detailValueCls}>{value}</span>
    </div>
  );
};

const CompactCell = ({ children, className = '' }) => (
  <div className={`${compactCellCls} ${className}`.trim()}>{children}</div>
);

const CustomerCareDetailsPanel = ({ submission }) => (
  <dl className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
    {CUSTOMER_CARE_SUBMISSION_FIELD_DEFINITIONS.map(({ key, label }) => {
      const value = getCustomerCareSubmissionFieldValue(submission, key);

      return (
        <div key={key} className="min-w-0">
          <dt className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            {label}
          </dt>
          <dd className="mt-1 break-words text-sm text-zinc-900 dark:text-zinc-100">
            {hasDetailValue(value) ? value : '-'}
          </dd>
        </div>
      );
    })}
  </dl>
);

const SelectControl = ({ id, label, value, onChange, options }) => (
  <label htmlFor={id} className={labelCls}>
    {label}
    <select
      id={id}
      name={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const CustomerCare = () => {
  const history = useNavigate();
  const { user } = useContext(firebaseAuth);
  const [settings, setSettings] = useState(() => readCustomerCareDashboardSettings());
  const [expandedRows, setExpandedRows] = useState({});
  const [data, setData] = useReducer(reducer, {
    submissions: [],
    isFetching: true,
    message: null,
  });

  const canExport = Number(user?.role) === 1 || user?.crmManager === true;
  const isDefaultView = hasDefaultCustomerCareDashboardSettings(settings);

  const fetchSubmissions = useCallback(async () => {
    const ninetyDaysAgo = moment().subtract(90, 'days').toDate();
    setData({ isFetching: true, message: null });

    try {
      const querySnapshot = await db
        .collection('submissions')
        .where('createdAt', '>=', ninetyDaysAgo)
        .orderBy('createdAt', 'desc')
        .get();
      const submissions = [];

      querySnapshot.forEach((doc) => {
        const submission = doc.data();
        submission.id = doc.id;
        submissions.push(submission);
      });

      setData({
        submissions,
        isFetching: false,
        message: null,
      });
    } catch (error) {
      console.log('Error getting customer care submissions: ', error);
      setData({
        isFetching: false,
        message: 'Could not load customer care submissions.',
      });
    }

  }, []);

  useEffect(() => {
    if (!user?.isAuthenticated) {
      history('/manage/sign-in');
      return;
    }
    if (!canExport) {
      history('/manage/permissions');
      return;
    }
    void fetchSubmissions();
    firebase.functions().httpsCallable('appV2/cleanupCustomerCare')({})
      .catch((error) => console.error('Customer Care cleanup failed:', error));
  }, [fetchSubmissions, history, user?.isAuthenticated, canExport]);

  useEffect(() => {
    writeCustomerCareDashboardSettings(settings);
  }, [settings]);

  const updateDashboardSetting = (key, value) => {
    setSettings((prev) =>
      normalizeCustomerCareDashboardSettings({
        ...prev,
        [key]: value,
        currentPage: key === 'currentPage' ? value : 1,
      }),
    );
  };

  const resetDashboardSettings = () => {
    clearCustomerCareDashboardSettings();
    setSettings(DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS);
  };

  const cycleSort = (key) => {
    setSettings((prev) => {
      const nextDir =
        prev.sortKey === key
          ? prev.sortDir === 'asc'
            ? 'desc'
            : 'asc'
          : defaultSortDirForKey(key);

      return normalizeCustomerCareDashboardSettings({
        ...prev,
        sortKey: key,
        sortDir: nextDir,
        currentPage: 1,
      });
    });
  };

  const sortAriaLabel = (key, label) => {
    if (settings.sortKey !== key) return `Sort by ${label}`;
    if (settings.sortDir === 'asc') {
      return `Sorted by ${label} ascending; click to sort descending`;
    }
    return `Sorted by ${label} descending; click to sort ascending`;
  };

  const SortableHeader = ({ sortKey, label }) => (
    <th scope="col" className={thCls}>
      <button
        type="button"
        onClick={() => cycleSort(sortKey)}
        aria-label={sortAriaLabel(sortKey, label)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-900 dark:hover:text-zinc-100"
      >
        {label}
        <SortIndicator active={settings.sortKey === sortKey} dir={settings.sortDir} />
      </button>
    </th>
  );

  const filteredSubmissions = useMemo(
    () => getFilteredCustomerCareSubmissions(data.submissions, settings),
    [data.submissions, settings],
  );

  const sortedAllSubmissions = useMemo(
    () => sortCustomerCareSubmissions(data.submissions, settings),
    [data.submissions, settings],
  );

  const totalPages = Math.max(1, Math.ceil(filteredSubmissions.length / settings.pageSize));
  const currentPage = Math.min(settings.currentPage, totalPages);
  const pageStart = filteredSubmissions.length ? (currentPage - 1) * settings.pageSize : 0;
  const pageEnd = Math.min(pageStart + settings.pageSize, filteredSubmissions.length);
  const pagedSubmissions = filteredSubmissions.slice(pageStart, pageEnd);
  const selectedDateRange =
    CUSTOMER_CARE_DATE_RANGES.find((option) => option.value === settings.dateRangeDays) ||
    CUSTOMER_CARE_DATE_RANGES[2];

  useEffect(() => {
    if (settings.currentPage > totalPages) {
      setSettings((prev) =>
        normalizeCustomerCareDashboardSettings({
          ...prev,
          currentPage: totalPages,
        }),
      );
    }
  }, [settings.currentPage, totalPages]);

  const goToPage = (page) => {
    updateDashboardSetting('currentPage', Math.min(Math.max(page, 1), totalPages));
  };

  const toggleRow = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const exportSubmissions = (rows, scope) => {
    if (!rows.length) {
      window.alert('No customer care submissions match this export.');
      return;
    }

    const exportRows = buildCustomerCareExportRows(rows);
    const csv = buildCsv(exportRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `customer-care-${scope}-${moment().format('YYYYMMDD-HHmm')}.csv`);
  };

  const renderPagination = () => (
    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="inline-flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
        <FunnelIcon className="size-4" aria-hidden="true" />
        <span>
          {filteredSubmissions.length === 0
            ? 'No matching submissions'
            : `Showing ${pageStart + 1}-${pageEnd} of ${filteredSubmissions.length} matching submissions`}
          {' '}
          ({data.submissions.length} loaded from the last 90 days)
        </span>
      </div>

      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          className={iconBtnCls}
          onClick={() => goToPage(1)}
          disabled={currentPage === 1}
          aria-label="First page"
          title="First page"
        >
          <ChevronDoubleLeftIcon className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          className={iconBtnCls}
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
          title="Previous page"
        >
          <ChevronLeftIcon className="size-4" aria-hidden="true" />
        </button>
        <span className="min-w-24 text-center text-sm text-zinc-600 dark:text-zinc-300">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          className={iconBtnCls}
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
          title="Next page"
        >
          <ChevronRightIcon className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          className={iconBtnCls}
          onClick={() => goToPage(totalPages)}
          disabled={currentPage === totalPages}
          aria-label="Last page"
          title="Last page"
        >
          <ChevronDoubleRightIcon className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
            Customer Care Submissions
          </h1>
          <div className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            <label className="font-medium text-zinc-900 dark:text-zinc-100">
              {selectedDateRange.label}:
            </label>{' '}
            <span>
              {filteredSubmissions.length} of {data.submissions.length}
            </span>
            {!isDefaultView && (
              <span className="ml-2 text-zinc-500 dark:text-zinc-400">Custom view</span>
            )}
          </div>
        </div>

        {canExport && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={primaryBtnCls}
              disabled={data.isFetching || filteredSubmissions.length === 0}
              onClick={() => exportSubmissions(filteredSubmissions, 'filtered')}
            >
              <ArrowDownTrayIcon className="size-4" aria-hidden="true" />
              Export filtered
            </button>
            <button
              type="button"
              className={secondaryBtnCls}
              disabled={data.isFetching || sortedAllSubmissions.length === 0}
              onClick={() => exportSubmissions(sortedAllSubmissions, 'last-90-days')}
            >
              <ArrowDownTrayIcon className="size-4" aria-hidden="true" />
              Export all
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-12 md:items-end">
        <div className="md:col-span-4">
          <label htmlFor="ccSearch" className={labelCls}>
            Search
          </label>
          <input
            id="ccSearch"
            type="text"
            className={inputCls}
            name="Search"
            value={settings.search}
            onChange={(e) => updateDashboardSetting('search', e.target.value)}
            placeholder="Search BP ID, dealer, VIN, email, address, or UTM data."
          />
        </div>

        <div className="md:col-span-2">
          <SelectControl
            id="ccDateRange"
            label="Date range"
            value={settings.dateRangeDays}
            onChange={(value) => updateDashboardSetting('dateRangeDays', value)}
            options={CUSTOMER_CARE_DATE_RANGES}
          />
        </div>

        <div className="md:col-span-2">
          <SelectControl
            id="ccType"
            label="CC Type"
            value={settings.ccType}
            onChange={(value) => updateDashboardSetting('ccType', value)}
            options={CUSTOMER_CARE_TYPE_OPTIONS}
          />
        </div>

        <div className="md:col-span-2">
          <SelectControl
            id="sentToCap"
            label="Sent to C@P"
            value={settings.sentToCap}
            onChange={(value) => updateDashboardSetting('sentToCap', value)}
            options={sentToCapOptions}
          />
        </div>

        <div className="md:col-span-1">
          <SelectControl
            id="ccRowsPerPage"
            label="Rows"
            value={settings.pageSize}
            onChange={(value) => updateDashboardSetting('pageSize', Number(value))}
            options={CUSTOMER_CARE_PAGE_SIZE_OPTIONS.map((value) => ({
              value,
              label: String(value),
            }))}
          />
        </div>

        <div className="md:col-span-1">
          <button
            type="button"
            className={secondaryBtnCls}
            onClick={resetDashboardSettings}
          >
            <ArrowPathIcon className="size-4" aria-hidden="true" />
            Reset / Clear
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {data.isFetching && <div className={alertCls}>Loading submissions.</div>}
        {data.message && <div className={alertCls}>{data.message}</div>}
      </div>

      {renderPagination()}

      <div className="mt-4 overflow-x-auto rounded-lg ring-1 ring-zinc-200 dark:ring-zinc-700">
        <table className="min-w-[1280px] divide-y divide-zinc-200 bg-zinc-50 dark:divide-zinc-700 dark:bg-zinc-900">
          <thead className="bg-zinc-100 dark:bg-zinc-800">
            <tr>
              <th scope="col" className="w-12 px-2 py-2">
                <span className="sr-only">Details</span>
              </th>
              <SortableHeader sortKey="customercare_type" label="CC Type" />
              <SortableHeader sortKey="bpid" label="BP ID" />
              <SortableHeader sortKey="dealer" label="Dealer" />
              <SortableHeader sortKey="vin" label="VIN" />
              <SortableHeader sortKey="email" label="Email" />
              <SortableHeader sortKey="email_optin" label="Opt-In" />
              <SortableHeader sortKey="mobile_phone" label="Mobile Phone" />
              <SortableHeader sortKey="address" label="Address" />
              <SortableHeader sortKey="vehicle" label="Vehicle Info" />
              <SortableHeader sortKey="additional" label="Additional Data" />
              <SortableHeader sortKey="createdAt" label="Created" />
              <SortableHeader sortKey="sentToCap" label="Sent to C@P" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {pagedSubmissions.length === 0 && (
              <tr>
                <td
                  colSpan={13}
                  className="px-4 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400"
                >
                  No customer care submissions match the current dashboard settings.
                </td>
              </tr>
            )}
            {pagedSubmissions.map((r, index) => {
              const rowKey = r.id || `${currentPage}-${pageStart + index}`;
              const isExpanded = Boolean(expandedRows[rowKey]);
              const rowLabel = r.email || r.bpid || r.vin || 'submission';

              return (
                <React.Fragment key={rowKey}>
                  <tr>
                    <td className="align-top px-2 py-2">
                      <button
                        type="button"
                        className={iconBtnCls}
                        onClick={() => toggleRow(rowKey)}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${rowLabel}`}
                        title={`${isExpanded ? 'Collapse' : 'Expand'} details`}
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="size-4" aria-hidden="true" />
                        ) : (
                          <ChevronRightIcon className="size-4" aria-hidden="true" />
                        )}
                      </button>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>{r.customercare_type}</CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>{r.bpid}</CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>{r.dealer}</CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>{r.vin}</CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>
                        <DetailLine label="Email" value={r.email} />
                        <DetailLine label="Confirm" value={r.confirm_email} />
                        <DetailLine label="Auto" value={formatCustomerCareBoolean(r.email_auto)} />
                      </CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>{formatCustomerCareBoolean(r.email_optin)}</CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>{r.mobile_phone}</CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell className="whitespace-pre-line">
                        <DetailLine label="Type" value={r.address_type} />
                        <DetailLine label="Company" value={r.company} />
                        {formatCustomerCareAddress(r)}
                        <DetailLine label="Country" value={r.country} />
                      </CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>
                        <DetailLine label="Status" value={r.vehicle_status} />
                        <DetailLine
                          label="Date"
                          value={[r.vehicle_status_month, r.vehicle_status_year]
                            .filter(Boolean)
                            .join(' ')}
                        />
                      </CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>
                        <DetailLine label="Record ID" value={r.id} />
                        <DetailLine label="QR" value={r.qr} />
                        <DetailLine label="Type" value={r.type} />
                        <DetailLine label="Importer" value={r.importer} />
                        <DetailLine label="SAP Outbound ID" value={r.sap_outbound_id} />
                        <DetailLine label="UTM Source" value={r.utm_source} />
                        <DetailLine label="UTM Medium" value={r.utm_medium} />
                        <DetailLine label="UTM Campaign" value={r.utm_campaign} />
                        <DetailLine label="UTM Term" value={r.utm_term} />
                        <DetailLine label="UTM Content" value={r.utm_content} />
                        {!formatCustomerCareAdditionalData(r) && <span />}
                      </CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>
                        <DetailLine
                          label="Created"
                          value={formatCustomerCareDate(r.createdAt) || r.createdDate}
                        />
                        <DetailLine
                          label="Updated"
                          value={formatCustomerCareDate(r.updatedAt) || r.updatedDate}
                        />
                      </CompactCell>
                    </td>
                    <td className={tdCls}>
                      <CompactCell>{formatCustomerCareBoolean(r.sentToCap)}</CompactCell>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-zinc-50 dark:bg-zinc-950">
                      <td colSpan={13} className="px-4 py-4">
                        <CustomerCareDetailsPanel submission={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {renderPagination()}

    </div>
  );
};

export default CustomerCare;
